<!--
  DependentDisclosure.vue

  Replaces the legacy dim-and-disable cascade everywhere a parent toggle
  gates child UI. When `parent` is true, the default slot renders inline.
  When `parent` is false, children are NOT rendered (not merely hidden) --
  a single flat labeled row shows `inertText` instead.

  This is the canonical implementation of the "dependent disclosure pattern"
  described in planning/design-handoffs/handoff_2026-05-09/
  2026-05-29-campaign-editor/PLAN.md (section "Dependent disclosure pattern").

  Props:
    parent      (Boolean, required) -- the controlling toggle's current state.
                Pass the v-model value of the toggle that gates this child UI.
    parentLabel (String, required)  -- human-readable name for what the toggle
                controls (e.g. "Payment methods"). Rendered as the row label
                in both expanded and collapsed states.
    inertText   (String, required)  -- short descriptor shown in the collapsed
                row (e.g. "Off - prices hidden"). Communicates the off-state
                to users and screen readers without rendering the full child UI.

  Slots:
    default -- the child UI to reveal when parent=true. Not rendered at all
               when parent=false.

  A11y:
    - The toggle sentinel element gets aria-controls pointing at the
      disclosure region's stable id, and aria-expanded mirroring `parent`.
    - The collapsed inert row is NOT aria-hidden -- the label communicates
      the off-state to screen readers.
    - The disclosure region carries role="region" and an aria-label derived
      from parentLabel.

  data-testid values:
    dependent-disclosure-toggle   -- the sentinel element carrying aria-controls
    dependent-disclosure-region   -- the div wrapping the default slot (expanded only)
    dependent-disclosure-inert-row -- the flat labeled row (collapsed only)
    dependent-disclosure-inert-text -- the inertText span inside the inert row
-->
<template>
  <div class="dependent-disclosure">
    <!--
      Toggle sentinel. This element does not contain the interactive toggle
      itself -- the toggle lives outside this component in the parent (the
      parent's v-model drives the `parent` prop). This sentinel exists solely
      to carry aria-controls so that assistive technology can announce the
      relationship between the toggle and the region it controls.

      It is visually hidden (width/height 0, overflow hidden, position
      absolute) so it has no layout impact while remaining in the
      accessibility tree.
    -->
    <span
      :id="toggleId"
      :aria-controls="parent ? regionId : null"
      :aria-expanded="String(parent)"
      data-testid="dependent-disclosure-toggle"
      class="dependent-disclosure__toggle-sentinel"
      aria-hidden="false"
    />

    <!-- Expanded state: render the full child UI. -->
    <div
      v-if="parent"
      :id="regionId"
      role="region"
      :aria-label="parentLabel"
      data-testid="dependent-disclosure-region"
      class="dependent-disclosure__region"
    >
      <slot />
    </div>

    <!-- Collapsed state: flat labeled row. NOT aria-hidden (label communicates state). -->
    <div
      v-else
      data-testid="dependent-disclosure-inert-row"
      class="dependent-disclosure__inert-row"
      :aria-label="`${parentLabel}: ${inertText}`"
    >
      <span class="dependent-disclosure__inert-label">{{ parentLabel }}</span>
      <span
        data-testid="dependent-disclosure-inert-text"
        class="dependent-disclosure__inert-text"
      >{{ inertText }}</span>
    </div>
  </div>
</template>

<script>
// Module-global monotonic counter for unique aria ids. Safe here because this
// app is client-rendered only (no SSR in the stack, so no hydration id mismatch
// to worry about). If this ever moves to Composition API, prefer Vue 3.5 useId().
let _instanceCounter = 0

export default {
  name: 'DependentDisclosure',

  props: {
    /**
     * The controlling toggle's current state. When true, the default slot
     * renders. When false, the inert labeled row renders instead.
     */
    parent: {
      type: Boolean,
      required: true,
    },

    /**
     * Human-readable name for what the toggle controls. Rendered in both
     * the expanded row header and the collapsed inert row label.
     * Example: "Payment methods"
     */
    parentLabel: {
      type: String,
      required: true,
    },

    /**
     * Short descriptor shown in the collapsed row. Communicates the
     * off-state to users and screen readers.
     * Example: "Off - prices hidden"
     */
    inertText: {
      type: String,
      required: true,
    },
  },

  data() {
    _instanceCounter += 1
    const id = _instanceCounter
    return {
      /**
       * Stable ids for aria-controls wiring. Each instance gets a unique
       * suffix so multiple DependentDisclosure components on the same page
       * do not share ids.
       */
      regionId: `dependent-disclosure-region-${id}`,
      toggleId: `dependent-disclosure-toggle-${id}`,
    }
  },
}
</script>

<style scoped>
/*
  Toggle sentinel: removed from visual layout, kept in the a11y tree.
  position:absolute + clip combination ensures it contributes zero layout
  pixels while remaining focusable/announceable.
*/
.dependent-disclosure__toggle-sentinel {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/*
  Inert row: flat single-line layout matching the prototype's
  "Off - prices hidden" treatment in redesign.jsx HiddenStack().
  Uses design tokens from colors_and_type.css where available.
*/
/* NO blanket `opacity` here, deliberately. This row is "inert" in the sense
   that the dependent control is collapsed -- but it is NOT a disabled
   control, and its text carries real state the user needs to read (the
   parentLabel plus an inertText like "Off - prices hidden"). It is
   intentionally not aria-hidden for exactly that reason (see the template
   note above).

   A container `opacity: 0.7` composites EVERY descendant, including the
   pill's own background, which put the pill's text at 2.81:1 -- well below
   AA for content a user is expected to read. The de-emphasis is now carried
   by the token scale and the muted pill background instead, which reads as
   "secondary" without dragging the text under the legibility floor. */
.dependent-disclosure__inert-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: var(--tf-surface, #ffffff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 12px);
}

.dependent-disclosure__inert-label {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  /* fg-3 rather than fg-2: with the row's blanket opacity removed (see
     above), fg-2 would render this collapsed row at the same weight as an
     active section title. fg-3 keeps it legibly secondary -- 8.19:1 on
     white, comfortably above AA, but a visible step below primary copy. */
  color: var(--tf-fg-3, #4f4f4f);
  font-family: var(--tf-font-sans, 'Roboto', sans-serif);
}

/*
  Inert text pill: matches the "Off - prices hidden" badge in the prototype
  (redesign.jsx line ~483): uppercase, muted bg, small tight type.
*/
.dependent-disclosure__inert-text {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: var(--tf-radius-pill, 9999px);
  background: var(--tf-surface-dark, #e8eff5);
  color: var(--tf-fg-4, #757575);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-family: var(--tf-font-sans, 'Roboto', sans-serif);
}

/* Region wrapper: no chrome of its own -- the slot provides its own layout. */
.dependent-disclosure__region {
  /* intentionally minimal -- child slot owns its layout */
}
</style>
